import { Fragment, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { RadioGroup } from '@headlessui/react'
import { useRouter } from 'next/router'

import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'
import { Shimmer } from '@wartek-id/shimmer'

import { PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import {
  useAPIContentKinerjaUtama,
  useAPIKinerja,
} from 'api/kinerja/useAPIKinerja'
import { assetUrl } from 'utils/url'
import { useUpsertPerencanaanPegawai } from 'app/KinerjaShared/hooks/useUpsertPerencanaanPegawai'
import { PAMONG_PRAKTIK_KINERJA_INDIKATOR_IMAGE } from 'app/KinerjaPamongPage/constants/praktik-kinerja'

import type { KinerjaPraktikPembelajaranDimensi } from 'types/kinerja'
import type { UpsertSKPPayload } from 'types/kinerja'

type PraktikKinerjaProps = {
  onNext: () => void
}

export function PraktikKinerja({ onNext }: PraktikKinerjaProps) {
  const router = useRouter()

  // -----------------------------------------
  // Get perencanaan data
  // -----------------------------------------
  const { data: perencanaanData, isLoading: isPerencanaanLoading } =
    useAPIKinerja(
      {
        staleTime: 60 * 60 * 1000, // 1 hour,
      },
      {
        tipe: 'SPNF',
      }
    )

  const currentPraktikKinerja = perencanaanData?.data?.praktikPembelajaran

  // -----------------------------------------
  // Get Praktik Kinerja content
  // -----------------------------------------
  const {
    data: dataContentKinerjaUtama,
    isLoading: isLoadingContentKinerjaUtama,
  } = useAPIContentKinerjaUtama(
    {
      tipe: 'SPNF',
    },
    {
      staleTime: 6 * 60 * 60 * 1000, // 6 hour
      enabled: true,
    }
  )

  const indikatorDimensiList: Array<Array<PraktikKinerjaItemProps>> =
    useMemo(() => {
      // Sort dimensi by skor ascending
      const sortedDimensiList =
        dataContentKinerjaUtama?.data?.praktikPembelajaran?.sort(
          (a, b) => a.skor - b.skor
        ) || []

      let resolvedList: Array<Array<PraktikKinerjaItemProps>> = []

      sortedDimensiList.forEach((dimensi) => {
        const isRecommended = isRecommendedDimensi(dimensi)

        const indikatorList = dimensi.indikator.map((indikator) => {
          return {
            id: String(indikator.id),
            title: indikator.nama,
            description: indikator.definisi,
            dimensiSkor: dimensi.skor,
            dimensiNama: dimensi.dimensiNama,
            isRecommended,
          }
        })

        resolvedList.push(indikatorList)
      })

      return resolvedList
    }, [dataContentKinerjaUtama?.data?.praktikPembelajaran])

  // -----------------------------------------
  // Handle form data
  // -----------------------------------------
  const [selectedPraktikKinerjaId, setSelectedPraktikKinerjaId] =
    useState<string>(undefined)

  // Sync with perencanaan data
  useEffect(() => {
    if (currentPraktikKinerja) {
      setSelectedPraktikKinerjaId(String(currentPraktikKinerja))
    }
  }, [currentPraktikKinerja])

  const isDirty = useMemo(() => {
    return selectedPraktikKinerjaId != String(currentPraktikKinerja)
  }, [currentPraktikKinerja, selectedPraktikKinerjaId])

  const isValidated = !!selectedPraktikKinerjaId

  // -----------------------------------------
  // Handle actions
  // -----------------------------------------
  const { handleUpsertMutation, isLoading: isLoadingUpsertPerencanaan } =
    useUpsertPerencanaanPegawai()
  const payload: UpsertSKPPayload = useMemo(
    () => ({
      id: perencanaanData?.data?.id,
      praktikPembelajaran: Number(selectedPraktikKinerjaId),
      tipe: 'SPNF',
    }),
    [perencanaanData, selectedPraktikKinerjaId]
  )

  const handleSaveToDraft = () => {
    // Do the upsert mutation
    handleUpsertMutation(payload, {
      shouldInvalidateCache: true,
      skip: !isDirty,
      showSuccessMessage: true,
      onSuccess: () => router.push(PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH),
    })
  }

  const handleNext = () => {
    handleUpsertMutation(payload, {
      shouldInvalidateCache: true,
      skip: !isDirty,
      onSuccess: onNext,
    })
  }
  // -----------------------------------------
  // Other derivatives
  // -----------------------------------------
  const isLoadingQueries = isPerencanaanLoading || isLoadingContentKinerjaUtama
  const isLoading = isLoadingQueries || isLoadingUpsertPerencanaan

  return (
    <>
      <div className="p-9">
        <div className="space-y-1 mb-9">
          <Text variant="heading-md" as="h3">
            Pilih 1 indikator untuk menjadi fokus peningkatan kinerja Anda
          </Text>

          <Text color="subdued">
            Diskusikan dengan atasan agar pilihan Anda turut mendukung
            peningkatan kinerja Satdik.
          </Text>
        </div>

        {isLoading ? (
          <div className="space-y-6">
            <Shimmer height="160px" width="100%" />
            <Shimmer height="160px" width="100%" />
            <Shimmer height="160px" width="100%" />
            <Shimmer height="160px" width="100%" />
            <Shimmer height="160px" width="100%" />
          </div>
        ) : (
          <RadioGroup
            onChange={setSelectedPraktikKinerjaId}
            value={selectedPraktikKinerjaId}
          >
            <div className="space-y-6 max-w-5xl">
              {indikatorDimensiList.map((indikatorGroup, groupIndex) => (
                <div className="grid grid-cols-3 gap-6" key={groupIndex}>
                  {indikatorGroup.map((item) => (
                    <PraktikKinerjaItem {...item} key={item.id} />
                  ))}
                </div>
              ))}
            </div>
          </RadioGroup>
        )}
      </div>
      <div className="border-t border-subdued px-6 py-4 flex justify-end gap-5">
        <Button
          size="lg"
          color="white"
          onClick={handleSaveToDraft}
          disabled={isPerencanaanLoading || !isDirty}
        >
          Simpan draf
        </Button>

        <Button
          color="black"
          size="lg"
          disabled={
            isPerencanaanLoading || isLoadingUpsertPerencanaan || !isValidated
          }
          onClick={handleNext}
        >
          Ke Pengembangan Kompetensi
        </Button>
      </div>
    </>
  )
}

function isRecommendedDimensi(dimensi: KinerjaPraktikPembelajaranDimensi) {
  return dimensi.skor === 1
}

type PraktikKinerjaItemProps = {
  id: string
  title: string
  description: string
  dimensiSkor: number
  dimensiNama: string
  isRecommended: boolean
}

const DIMENSI_SKOR_BADGE_STYLES: Record<number, string> = {
  1: 'bg-surface-critical-subdued text-critical',
  2: 'bg-surface-warning-subdued text-yellow-700',
  3: 'bg-surface-success-subdued text-success',
}

function PraktikKinerjaItem({
  id,
  title,
  description,
  dimensiSkor,
  dimensiNama,
  isRecommended,
}: PraktikKinerjaItemProps) {
  const labelElementId = 'praktik-kinerja-item-label-' + id

  const badgeStyle =
    DIMENSI_SKOR_BADGE_STYLES[dimensiSkor] ??
    'bg-surface-informational-subdued text-blue-50'

  return (
    <RadioGroup.Option value={id} as={Fragment}>
      {({ checked }) => (
        <div
          className={clsx(
            'rounded border pt-10 pb-4 px-4 outline-none cursor-pointer relative',
            'flex flex-col gap-4 justify-between items-start',
            checked
              ? 'bg-surface-selected-default border-blue-60'
              : 'bg-white border-subdued'
          )}
          aria-labelledby={labelElementId}
        >
          {isRecommended && (
            <div className="p-2 bg-surface-informational-subdued rounded-tr rounded-bl absolute top-0 right-0">
              <Text variant="body-sm-bold" className="!text-blue-60">
                Rekomendasi
              </Text>
            </div>
          )}
          <div className="space-y-2">
            <img
              className="flex-shrink-0 flex-grow-0"
              src={assetUrl(
                `/illustrations/${PAMONG_PRAKTIK_KINERJA_INDIKATOR_IMAGE[title]}`
              )}
              alt=""
              height={84}
              width={84}
            />

            <Text variant="heading-sm" id={labelElementId}>
              {title}
            </Text>
            <Text variant="body" color="subdued">
              {description}
            </Text>
          </div>
          <div className={clsx('px-2 py-1 rounded', badgeStyle)}>
            <Text variant="body-sm" color="inherit">
              Dimensi: {dimensiNama}
            </Text>
          </div>
        </div>
      )}
    </RadioGroup.Option>
  )
}
